/**
 * Behavior strategies based on unit skill types
 * Allows different decision-making for ranged vs melee units
 */

import { BattleUnit } from '../../types/battle.types';
import { UNIT_BEHAVIOR } from './utils/AIConfig';

export type UnitBehaviorType = 'MELEE' | 'RANGED' | 'HYBRID';

export interface BehaviorStrategy {
  type: UnitBehaviorType;
  preferredDistance: number;
  minDistance: number;
  maxDistance: number;
  shouldKite: boolean; // Retreat and attack from distance?
}

export class BehaviorAnalyzer {
  /**
   * Determine unit behavior based on available skills
   * Considers the range of skills to classify as melee, ranged, or hybrid
   */
  static analyzeBehavior(unit: BattleUnit): BehaviorStrategy {
    const skills = unit.skills;
    
    if (skills.length === 0) {
      return this.createMeleeStrategy();
    }

    const rangedSkills = skills.filter(s => s.range > UNIT_BEHAVIOR.MELEE_THRESHOLD);
    // const meleeSkills = skills.filter(s => s.range <= UNIT_BEHAVIOR.MELEE_THRESHOLD);

    if (rangedSkills.length > 0) {
        return this.createRangedStrategy();
    } else {
        return this.createMeleeStrategy();
    }

    // if (rangedSkills.length > 0 && meleeSkills.length === 0) {
    //   return this.createRangedStrategy();
    // }

    // if (meleeSkills.length > 0 && rangedSkills.length === 0) {
    //   return this.createMeleeStrategy();
    // }

    // return this.createHybridStrategy();
  }

  private static createMeleeStrategy(): BehaviorStrategy {
    return {
      type: 'MELEE',
      preferredDistance: 1,
      minDistance: 0,
      maxDistance: 2,
      shouldKite: false,
    };
  }

  private static createRangedStrategy(): BehaviorStrategy {
    return {
      type: 'RANGED',
      preferredDistance: UNIT_BEHAVIOR.RANGED_PREFERRED_DISTANCE,
      minDistance: UNIT_BEHAVIOR.RANGED_MIN_DISTANCE,
      maxDistance: 6,
      shouldKite: true,
    };
  }

  private static createHybridStrategy(): BehaviorStrategy {
    return {
      type: 'HYBRID',
      preferredDistance: 4,
      minDistance: 1,
      maxDistance: 12,
      shouldKite: true,
    };
  }

  /**
   * Get max range of all skills
   * Useful for determining when to stop advancing
   */
  static getMaxSkillRange(unit: BattleUnit): number {
    if (unit.skills.length === 0) return 1;
    return Math.max(...unit.skills.map(s => s.range));
  }

  /**
   * Check if unit can perform melee action
   */
  static canPerformMelee(unit: BattleUnit): boolean {
    return unit.skills.some(s => s.range <= UNIT_BEHAVIOR.MELEE_THRESHOLD);
  }

  /**
   * Check if unit can perform ranged action
   */
  static canPerformRanged(unit: BattleUnit): boolean {
    return unit.skills.some(s => s.range > UNIT_BEHAVIOR.MELEE_THRESHOLD);
  }
}
